import React, { useState, useEffect } from "react";
import {
    Accordion, AccordionSummary, AccordionDetails, Typography, Box,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button,
    TextField, List, ListItem, ListItemText
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { client } from "../api/Api";
import { useNavigate } from "react-router-dom";

const CategoriesAccordion = () => {
    const [rootCategories, setRootCategories] = useState([]);
    const [childrenMap, setChildrenMap] = useState({});
    const [productsMap, setProductsMap] = useState({});
    const [expandedPath, setExpandedPath] = useState([]);
    const [editCat, setEditCat] = useState(null);
    const [editFields, setEditFields] = useState({ name: "", url: "", iconUrl: "" });
    const [editOpen, setEditOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        client.get("/category/0")
            .then(res => setRootCategories(res.data))
            .catch(err => console.error(err));
    }, []);

    // When expandedPath changes, load children and products for expanded category only
    useEffect(() => {
        if (expandedPath.length === 0) return;
        const lastExpandedId = expandedPath[expandedPath.length - 1];
        console.log("expandedPath changed", expandedPath, lastExpandedId);
        if (!childrenMap[lastExpandedId]) {
            client.get(`/category/${lastExpandedId}`).then(res =>
                setChildrenMap(prev => ({ ...prev, [lastExpandedId]: res.data }))
            );
        }
        console.log("expandedPath changed", expandedPath, lastExpandedId);
        if (!productsMap[lastExpandedId]) {
            client.get(`/products/${lastExpandedId}?size=20&page=0`).then(res =>
                setProductsMap(prev => ({ ...prev, [lastExpandedId]: res.data.products }))
            );
        }
        // eslint-disable-next-line
    }, [expandedPath]);

    const handleEditOpen = (cat) => {
        setEditCat(cat);
        setEditFields({
            name: cat.name || "",
            url: cat.url || "",
            iconUrl: cat.iconUrl || ""
        });
        setEditOpen(true);
    };

    const handleEditClose = () => {
        setEditOpen(false);
        setEditCat(null);
    };

    const handleEditChange = (e) => {
        setEditFields(f => ({ ...f, [e.target.name]: e.target.value }));
    };

    const saveEdit = async () => {
        if (!editCat) return;
        try {
            await client.put(`/category/${editCat.id}`, editFields);
            if (editCat.parentId === 0) {
                const res = await client.get("/category/0");
                setRootCategories(res.data);
            } else {
                const parent = editCat.parentId;
                const res = await client.get(`/category/${parent}`);
                setChildrenMap((prev) => ({ ...prev, [parent]: res.data }));
            }
        } catch (error) {
            alert("Failed to update category");
        }
        handleEditClose();
    };

    const renderCategories = (cats, depth = 0) => (
        <Box>
            {cats.map(cat => {
                const isExpanded = expandedPath[depth] === cat.id;
                return (
                    <Accordion
                        key={cat.alias || cat.id}
                        expanded={isExpanded}
                        onChange={(_, isExp) => {
                            let newPath;
                            if (isExp) {
                                newPath = [...expandedPath.slice(0, depth), cat.id];
                            } else {
                                newPath = expandedPath.slice(0, depth);
                            }
                            setExpandedPath(newPath);
                        }}
                        sx={{ ml: depth * 3 }}
                        disableGutters
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ flexGrow: 1 }}>{cat.name}</Typography>
                            <IconButton size="small" onClick={e => { e.stopPropagation(); handleEditOpen(cat); }}><EditIcon fontSize="inherit" /></IconButton>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0, bgcolor: "#f7f9fa" }}>
                            {childrenMap[cat.id] && childrenMap[cat.id].length > 0 &&
                                renderCategories(childrenMap[cat.id], depth + 1)
                            }
                            {/* Product List for this category */}
                            {productsMap[cat.id] && productsMap[cat.id].length > 0 && (
                                <Box sx={{ p: 2, mt: 1, mb: 1, bgcolor: "#e7e7ef", borderRadius: 2 }}>
                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                                        Products
                                    </Typography>
                                    <List dense>
                                        {productsMap[cat.id].map(prod => (
                                            <ListItem
                                                button
                                                key={prod.id}
                                                sx={{
                                                    borderRadius: 1,
                                                    mb: 0.5,
                                                    bgcolor: "#fff",
                                                    boxShadow: 1,
                                                    "&:hover": { bgcolor: "#e2f0fb" }
                                                }}
                                                onClick={() => navigate(`/admin/product/${prod.id}`)}
                                            >
                                                <ListItemText
                                                    primary={prod.name}
                                                    secondary={`ID: ${prod.id}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                            {productsMap[cat.id] && productsMap[cat.id].length === 0 && (
                                <Typography color="text.secondary" sx={{ pl: 2, pt: 1, pb: 1 }}>
                                    No products in this category.
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );

    return (
        <Box p={2}>
            <Typography variant="h6">Categories</Typography>
            {renderCategories(rootCategories)}
            <Dialog open={editOpen} onClose={handleEditClose}>
                <DialogTitle>Edit Category</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField label="Name" name="name" value={editFields.name} onChange={handleEditChange} />
                    <TextField label="URL" name="url" value={editFields.url} onChange={handleEditChange} />
                    <TextField label="Icon URL" name="iconUrl" value={editFields.iconUrl} onChange={handleEditChange} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditClose}>Cancel</Button>
                    <Button variant="contained" onClick={saveEdit}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
export default CategoriesAccordion;